import React, { useState, useCallback, useEffect } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import qs from 'qs';
import debounce from 'lodash/debounce';
import startCase from 'lodash/startCase';

import { FontAwesomeIcon as FA } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash';
import { faUpload } from '@fortawesome/free-solid-svg-icons/faUpload';
import { faDownload } from '@fortawesome/free-solid-svg-icons/faDownload';

import { useTranslation } from 'react-i18next';
import { resolveTo } from '~/commons/helpers';
import useHttpStatus from '~/commons/hooks/useHttpStatus';
import useComparable from '~/commons/hooks/useComparable';

import ListHttpTable from '~/commons/components/ListHttpTable.jsx';
import FormHttpBtn from '~/commons/components/FormHttpBtn.jsx';
import FormHttpFileBtn from '~/commons/components/FormHttpFileBtn.jsx';
import ModalConfirm from '~/commons/components/ModalConfirm.jsx';

const PageResource = ({
  api,
  sort,
  cols,
  searchable = false,
  filter = {},
  actions: {
    fileImport = false,
    fileExport = false,
    find = true,
    create = true,
    remove = true,
  } = {},
  basePath = '',
  onRowClick,
  ctrls = [],
  children = () => {},

}) => {
  const { hash } = useLocation();
  // console.log( hash );
  const history = useHistory();
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState('');
  const [fSearch, setFSearch] = useState('');
  const [searchBy, setSearchBy] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [res, req] = useHttpStatus();
  const [removeConfirm, setRemoveConfirm] = useState(false);
  const { t } = useTranslation();
  const debouncedSearch = useCallback(debounce(
    (v) => setFSearch(v),
    1500,
    { leading: true },
  ), []);
  let searchOpts;

  if (Array.isArray(searchable)) {
    const c = searchable.find((s) => s.value === searchBy);

    searchOpts = c?.options;
  }

  const bPath = typeof basePath === 'function'
    ? basePath
    : () => basePath;
  const f = { ...filter };

  if (searchable && fSearch) {
    f.search = fSearch;
    if (searchBy) f.searchBy = searchBy;
  }

  useEffect(() => {
    setSearchBy(
      Array.isArray(searchable)
        ? searchable[0].value || searchable[0]
        : '',
    );
  }, [useComparable(searchable)]);

  return (
    <div>
      {remove && (
        <ModalConfirm
          show={removeConfirm}
          onReject={() => setRemoveConfirm(false)}
          onConfirm={async () => {
            setRemoveConfirm(false);

            await req({
              url: api,
              method: 'delete',
              data: { _id: selected.map((s) => s._id) },
            });

            setRefresh(refresh + 1);
          }}
          {...({
            children: t('Are you sure you want to delete?'),
            ...(typeof remove === 'function' && remove({ selected })),
          })}
        />

      )}
      <div className="pb-3 clearfix">
        {create && (
          <Link
            to={resolveTo(bPath('new'), 'new')}
            className="btn btn-primary mr-2"
          >
            <FA icon={faPlus} fixedWidth />
          </Link>
        )}
        {fileImport && (
          <FormHttpFileBtn
            className="btn btn-secondary mr-2"
            icon={faUpload}
            onSuccess={() => setRefresh(refresh + 1)}
            customErrorAlert={fileImport.customErrorAlert}
            api={{
              url: `${api}/${
                typeof fileImport === 'string' ? fileImport : 'import'
              }`,
              ...fileImport,
              customErrorAlert: undefined,
            }}
            fileOpts={{
              accept: '.csv',
              resetOnSelect: fileImport.resetOnSelect,
            }}
            {...{ children: fileImport.children }}
          />
        )}
        {fileExport && (
          <a
            className="btn btn-secondary mr-2"
            href={`${typeof fileExport === 'object' ? fileExport.href : api + '/' + 
              (typeof fileExport === 'string' ? fileExport : 'export')
            }${
              Object.values(f).filter((s) => s).length
                ? `?${qs.stringify({ filter: f })}` : ''
            }`}
            {...(fileExport.target ? {target: fileExport.target} : {})}
          >
            <FA icon={faDownload} fixedWidth />
          </a>
        )}
        {remove && selected.length > 0 && (
          <FormHttpBtn
            res={res}
            className="btn btn-link"
            icon={faTrash}
            onClick={() => setRemoveConfirm(true)}
          />
        )}
        {children({
          selected,
          refresh: () => setRefresh(refresh + 1),
        })}
      </div>
      <ListHttpTable
        {...{
          api: { url: api },
          sort,
          cols: cols.map((v) => (v.children
            ? { ...v, children: t(v.children) }
            : { ...v, children: t(startCase(v.key)) })),
          filter: f,
          refresh,
          setRefresh,
          onSelect: remove
            ? (s) => { setSelected(s); }
            : undefined,
          ...(find ? {
            onRowClick: onRowClick || ((row) => {
              history.push(resolveTo(bPath(row), row._id));
            }),
          } : {}),
        }}
      >
        <div className="form-row">
          {searchable && (
            <div className="col">
              <div className="input-group">
                {Array.isArray(searchable) && (
                  <div className="input-group-prepend">
                    <select
                      name="searchBy"
                      className="custom-select"
                      value={searchBy}
                      onChange={(e) => {
                        setSearch('');
                        debouncedSearch('');
                        setSearchBy(e.target.value);
                      }}
                    >
                      {searchable.map((by) => (
                        <option key={by.value || by} value={by.value || by}>
                          {t(by.label || startCase(by))}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {searchOpts ? (
                  <select
                    className="custom-select"
                    value={search}
                    onChange={(e) => {
                      const { value } = e.target;

                      setSearch(value);
                      debouncedSearch(value);
                    }}
                  >
                    <option value="">{t('Show All')}</option>
                    {searchOpts.map((o) => (
                      <option key={o.value} value={o.value}>
                        {t(o.label)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    className="form-control"
                    placeholder={`${t('Search')}...`}
                    value={search}
                    onChange={(e) => {
                      const { value } = e.target;

                      setSearch(value);
                      debouncedSearch(value);
                    }}
                  />
                )}
              </div>
            </div>
          )}
          {ctrls.map((c, i) => (
            <div className="col-auto" key={i}>
              {c}
            </div>
          ))}
        </div>
      </ListHttpTable>
    </div>
  );
};

export default PageResource;
